import type {
  HarEntry,
  HarEntryDecodeStatusType,
  JsonValueType,
  WebGrpcDecodedData,
} from '@terra/shared-schemas-tracker';
import { z } from 'zod';
import {
  hasUsableRequestWebGrpcDecode,
  hasUsableResponseWebGrpcDecode,
} from './har-grpc-enrichment.utils';
import {
  formatHarRequestAsHttpMessage,
  formatHarResponseAsHttpMessage,
  type HarPostDataLike,
  type HarResponseContentLike,
} from './har-body-encoding.utils';

const PARTIAL_DECODE_PREFIX = '[partial decode — not all fields recovered]\n';

const HarHttpRequestSchema = z.object({
  method: z.string(),
  url: z.string(),
  httpVersion: z.string().optional(),
  headers: z.array(z.object({ name: z.string(), value: z.string() })),
  postData: z.custom<HarPostDataLike>().optional(),
});

const HarHttpResponseSchema = z.object({
  status: z.number(),
  statusText: z.string().optional(),
  httpVersion: z.string().optional(),
  headers: z.array(z.object({ name: z.string(), value: z.string() })),
  content: z.custom<HarResponseContentLike>(),
});

export type HarViewPreferredPart = {
  body: string;
  source: 'wire' | 'decoded';
  partial: boolean;
};

export type HarView = {
  original: {
    request: unknown;
    response?: unknown | null;
  };
  decoded: WebGrpcDecodedData | null;
  preferred: {
    request: HarViewPreferredPart;
    response: HarViewPreferredPart;
  };
  protocol: string | null;
  decodeStatus: HarEntryDecodeStatusType | null;
};

export type HarEntryWithView = {
  harEntry: HarEntry;
  view: HarView;
};

export type StoredHarDisplayEntry = {
  request: unknown;
  response?: unknown | null;
  webGrpcDecodedData?: WebGrpcDecodedData | null;
};

export type HarGrpcBodyView = 'decoded' | 'wire';

const usesDecodedBody = (
  decodeStatus: HarEntryDecodeStatusType | null | undefined,
  decodedValue: JsonValueType | null | undefined,
): decodedValue is JsonValueType =>
  decodedValue !== null &&
  decodedValue !== undefined &&
  (decodeStatus === 'DECODED' || decodeStatus === 'PARTIAL');

const formatDecodedBody = ({
  value,
  partial,
}: {
  value: JsonValueType;
  partial: boolean;
}): string => {
  const json = JSON.stringify(value, null, 2);

  return partial ? `${PARTIAL_DECODE_PREFIX}${json}` : json;
};

const resolveDecodedPreferredPart = ({
  decodedValue,
  decodeStatus,
  decodeError,
}: {
  decodedValue: JsonValueType | null | undefined;
  decodeStatus: HarEntryDecodeStatusType | null | undefined;
  decodeError: string | null | undefined;
}): HarViewPreferredPart | null => {
  if (usesDecodedBody(decodeStatus, decodedValue)) {
    return {
      body: formatDecodedBody({
        value: decodedValue,
        partial: decodeStatus === 'PARTIAL',
      }),
      source: 'decoded',
      partial: decodeStatus === 'PARTIAL',
    };
  }

  if (
    decodeStatus === 'PARTIAL' &&
    (decodedValue === null || decodedValue === undefined) &&
    decodeError
  ) {
    return {
      body: `${PARTIAL_DECODE_PREFIX}${decodeError}`,
      source: 'decoded',
      partial: true,
    };
  }

  return null;
};

const extractHttpMessageBody = ({ message }: { message: string }): string => {
  const separatorIndex = message.indexOf('\n\n');

  return separatorIndex === -1 ? '' : message.slice(separatorIndex + 2);
};

const replaceHttpMessageBody = ({
  message,
  body,
}: {
  message: string;
  body: string;
}): string => {
  const separatorIndex = message.indexOf('\n\n');
  const head =
    separatorIndex === -1 ? message : message.slice(0, separatorIndex);

  return body ? `${head}\n\n${body}` : head;
};

const parseHarHttpRequest = ({
  request,
}: {
  request: unknown;
}): z.infer<typeof HarHttpRequestSchema> | null => {
  const parsed = HarHttpRequestSchema.safeParse(request);

  return parsed.success ? parsed.data : null;
};

const parseHarHttpResponse = ({
  response,
}: {
  response: unknown;
}): z.infer<typeof HarHttpResponseSchema> | null => {
  const parsed = HarHttpResponseSchema.safeParse(response);

  return parsed.success ? parsed.data : null;
};

const resolvePreferredRequestPart = ({
  harEntry,
  decoded,
}: {
  harEntry: Pick<StoredHarDisplayEntry, 'request'>;
  decoded: WebGrpcDecodedData | null;
}): HarViewPreferredPart => {
  const decodedPreferred = resolveDecodedPreferredPart({
    decodedValue: decoded?.decodedRequest,
    decodeStatus: decoded?.status,
    decodeError: decoded?.error,
  });

  if (decodedPreferred) {
    return decodedPreferred;
  }

  const request = parseHarHttpRequest({ request: harEntry.request });

  if (!request) {
    return { body: '', source: 'wire', partial: false };
  }

  const wireMessage = formatHarRequestAsHttpMessage({ request });

  return {
    body: extractHttpMessageBody({ message: wireMessage }),
    source: 'wire',
    partial: false,
  };
};

const resolvePreferredResponsePart = ({
  harEntry,
  decoded,
}: {
  harEntry: Pick<StoredHarDisplayEntry, 'response'>;
  decoded: WebGrpcDecodedData | null;
}): HarViewPreferredPart => {
  const decodedPreferred = resolveDecodedPreferredPart({
    decodedValue: decoded?.decodedResponse,
    decodeStatus: decoded?.status,
    decodeError: decoded?.error,
  });

  if (decodedPreferred) {
    return decodedPreferred;
  }

  const response = parseHarHttpResponse({ response: harEntry.response });

  if (!response) {
    return { body: '', source: 'wire', partial: false };
  }

  const wireMessage = formatHarResponseAsHttpMessage({ response });

  return {
    body: extractHttpMessageBody({ message: wireMessage }),
    source: 'wire',
    partial: false,
  };
};

export const resolveHarView = ({
  harEntry,
  decoded,
}: {
  harEntry: StoredHarDisplayEntry;
  decoded?: WebGrpcDecodedData | null;
}): HarView => {
  const effectiveDecoded =
    decoded !== undefined ? decoded : (harEntry.webGrpcDecodedData ?? null);

  return {
    original: harEntry,
    decoded: effectiveDecoded,
    preferred: {
      request: resolvePreferredRequestPart({
        harEntry,
        decoded: effectiveDecoded,
      }),
      response: resolvePreferredResponsePart({
        harEntry,
        decoded: effectiveDecoded,
      }),
    },
    protocol: effectiveDecoded?.protocol ?? null,
    decodeStatus: effectiveDecoded?.status ?? null,
  };
};

export const formatHarRequestAsHttpMessageWithDecode = ({
  request,
  decodedRequest,
  decodeStatus,
  decodeError,
}: {
  request: z.infer<typeof HarHttpRequestSchema>;
  decodedRequest?: JsonValueType | null;
  decodeStatus?: HarEntryDecodeStatusType | null;
  decodeError?: string | null;
}): string => {
  const wireMessage = formatHarRequestAsHttpMessage({ request });
  const decodedPreferred = resolveDecodedPreferredPart({
    decodedValue: decodedRequest,
    decodeStatus,
    decodeError,
  });

  if (!decodedPreferred) {
    return wireMessage;
  }

  return replaceHttpMessageBody({
    message: wireMessage,
    body: decodedPreferred.body,
  });
};

export const formatHarResponseAsHttpMessageWithDecode = ({
  response,
  decodedResponse,
  decodeStatus,
  decodeError,
}: {
  response: z.infer<typeof HarHttpResponseSchema>;
  decodedResponse?: JsonValueType | null;
  decodeStatus?: HarEntryDecodeStatusType | null;
  decodeError?: string | null;
}): string => {
  const wireMessage = formatHarResponseAsHttpMessage({ response });
  const decodedPreferred = resolveDecodedPreferredPart({
    decodedValue: decodedResponse,
    decodeStatus,
    decodeError,
  });

  if (!decodedPreferred) {
    return wireMessage;
  }

  return replaceHttpMessageBody({
    message: wireMessage,
    body: decodedPreferred.body,
  });
};

export const formatHarRequestFromView = ({
  view,
}: {
  view: HarView;
}): string => {
  const request = parseHarHttpRequest({ request: view.original.request });

  if (!request) {
    return '';
  }

  return formatHarRequestAsHttpMessageWithDecode({
    request,
    decodedRequest: view.decoded?.decodedRequest,
    decodeStatus: view.decodeStatus,
    decodeError: view.decoded?.error,
  });
};

export const formatHarResponseFromView = ({
  view,
}: {
  view: HarView;
}): string => {
  const response = parseHarHttpResponse({ response: view.original.response });

  if (!response) {
    return '';
  }

  return formatHarResponseAsHttpMessageWithDecode({
    response,
    decodedResponse: view.decoded?.decodedResponse,
    decodeStatus: view.decodeStatus,
    decodeError: view.decoded?.error,
  });
};

export const hasHarDecodedBodyView = ({
  harEntry,
}: {
  harEntry: StoredHarDisplayEntry;
}): boolean => {
  const view = resolveHarView({ harEntry });

  return (
    view.preferred.request.source === 'decoded' ||
    view.preferred.response.source === 'decoded'
  );
};

export const formatStoredHarRequestWireForDisplay = ({
  harEntry,
}: {
  harEntry: StoredHarDisplayEntry;
}): string => {
  const request = parseHarHttpRequest({ request: harEntry.request });

  if (!request) {
    return '';
  }

  return formatHarRequestAsHttpMessage({ request });
};

export const formatStoredHarResponseWireForDisplay = ({
  harEntry,
}: {
  harEntry: StoredHarDisplayEntry;
}): string => {
  const response = parseHarHttpResponse({ response: harEntry.response });

  if (!response) {
    return '';
  }

  return formatHarResponseAsHttpMessage({ response });
};

export const resolveStoredHarRequestDisplayContent = ({
  harEntry,
  bodyView,
}: {
  harEntry: StoredHarDisplayEntry;
  bodyView: HarGrpcBodyView;
}): string => {
  if (bodyView === 'wire' && hasHarDecodedBodyView({ harEntry })) {
    return formatStoredHarRequestWireForDisplay({ harEntry });
  }

  return formatStoredHarRequestForDisplay({ harEntry });
};

export const resolveStoredHarResponseDisplayContent = ({
  harEntry,
  bodyView,
}: {
  harEntry: StoredHarDisplayEntry;
  bodyView: HarGrpcBodyView;
}): string => {
  if (bodyView === 'wire' && hasHarDecodedBodyView({ harEntry })) {
    return formatStoredHarResponseWireForDisplay({ harEntry });
  }

  return formatStoredHarResponseForDisplay({ harEntry });
};

export const formatStoredHarRequestForDisplay = ({
  harEntry,
}: {
  harEntry: StoredHarDisplayEntry;
}): string =>
  formatHarRequestFromView({
    view: resolveHarView({ harEntry }),
  });

export const formatStoredHarResponseForDisplay = ({
  harEntry,
}: {
  harEntry: StoredHarDisplayEntry;
}): string =>
  formatHarResponseFromView({
    view: resolveHarView({ harEntry }),
  });

export const attachHarViews = ({
  harEntries,
}: {
  harEntries: HarEntry[];
}): HarEntryWithView[] =>
  harEntries.map((harEntry) => ({
    harEntry,
    view: resolveHarView({ harEntry }),
  }));

export const getHarPreferredBodyText = ({
  harEntry,
  part,
  maxLength,
}: {
  harEntry: StoredHarDisplayEntry;
  part: 'request' | 'response';
  maxLength?: number;
}): string => {
  const view = resolveHarView({ harEntry });
  const preferred =
    part === 'request' ? view.preferred.request : view.preferred.response;
  const body = preferred.body;

  if (maxLength === undefined || body.length <= maxLength) {
    return body;
  }

  return `${body.slice(0, maxLength)}...[TRUNCATED]`;
};

export const extractHarDisplayBodyText = (message: string): string => {
  const separatorIndex = message.indexOf('\n\n');

  return separatorIndex === -1 ? '' : message.slice(separatorIndex + 2);
};

export const resolveHarEntryRequestDisplayContent = ({
  harEntry,
  bodyView,
  fallbackContent,
}: {
  harEntry?: StoredHarDisplayEntry | null;
  bodyView: HarGrpcBodyView;
  fallbackContent: string;
}): string => {
  if (!harEntry) {
    return fallbackContent;
  }

  if (bodyView === 'wire' && hasHarDecodedBodyView({ harEntry })) {
    return formatStoredHarRequestWireForDisplay({ harEntry });
  }

  if (
    bodyView === 'decoded' &&
    !hasUsableRequestWebGrpcDecode(harEntry.webGrpcDecodedData)
  ) {
    return fallbackContent;
  }

  return resolveStoredHarRequestDisplayContent({ harEntry, bodyView });
};

export const resolveHarEntryResponseDisplayContent = ({
  harEntry,
  bodyView,
  fallbackContent,
}: {
  harEntry?: StoredHarDisplayEntry | null;
  bodyView: HarGrpcBodyView;
  fallbackContent: string;
}): string => {
  if (!harEntry) {
    return fallbackContent;
  }

  if (bodyView === 'wire' && hasHarDecodedBodyView({ harEntry })) {
    return formatStoredHarResponseWireForDisplay({ harEntry });
  }

  if (
    bodyView === 'decoded' &&
    !hasUsableResponseWebGrpcDecode(harEntry.webGrpcDecodedData)
  ) {
    return fallbackContent;
  }

  return resolveStoredHarResponseDisplayContent({ harEntry, bodyView });
};

export const resolveHarEntryRequestBodyText = ({
  harEntry,
  bodyView,
  fallbackBody,
}: {
  harEntry?: StoredHarDisplayEntry | null;
  bodyView: HarGrpcBodyView;
  fallbackBody?: string;
}): string | undefined => {
  if (!harEntry) {
    return fallbackBody;
  }

  if (bodyView === 'wire' && hasHarDecodedBodyView({ harEntry })) {
    return extractHarDisplayBodyText(
      formatStoredHarRequestWireForDisplay({ harEntry }),
    );
  }

  if (
    bodyView === 'decoded' &&
    !hasUsableRequestWebGrpcDecode(harEntry.webGrpcDecodedData)
  ) {
    return fallbackBody;
  }

  const body = extractHarDisplayBodyText(
    resolveStoredHarRequestDisplayContent({ harEntry, bodyView }),
  );

  return body || fallbackBody;
};

export const resolveHarEntryResponseBodyText = ({
  harEntry,
  bodyView,
  fallbackBody,
}: {
  harEntry?: StoredHarDisplayEntry | null;
  bodyView: HarGrpcBodyView;
  fallbackBody: string;
}): string => {
  if (!harEntry) {
    return fallbackBody;
  }

  if (bodyView === 'wire' && hasHarDecodedBodyView({ harEntry })) {
    return extractHarDisplayBodyText(
      formatStoredHarResponseWireForDisplay({ harEntry }),
    );
  }

  if (
    bodyView === 'decoded' &&
    !hasUsableResponseWebGrpcDecode(harEntry.webGrpcDecodedData)
  ) {
    return fallbackBody;
  }

  const body = extractHarDisplayBodyText(
    resolveStoredHarResponseDisplayContent({ harEntry, bodyView }),
  );

  return body || fallbackBody;
};
